import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faExclamationTriangle,
    faSearch,
    faArchive,
    faCheckCircle,
    faClock,
    faTimesCircle,
    faUsers,
    faBuilding,
    faUpload,
    faFileInvoiceDollar,
    faUserPlus,
    faMapMarkedAlt,
    faFileShield,
    faTags,
    faIndustry,
    faUser,
    faReceipt,
    faShieldAlt,
    faBell
} from '@fortawesome/free-solid-svg-icons';
import HamsterFloatingButton from './HamsterFloatingButton';
import BackToTopButton from './BackToTopButton';
import RiskAlertsPopup from './CRM/RiskAlertsPopup';
import CRMDashboardMap from './CRM/CRMDashboardMap';
import ApprovalStatusModal from './CRM/ApprovalStatusModal';
import AdHocRiskCheckModal from './CRM/AdHocRiskCheckModal';
import CRMPartnerImportModal from './CRM/CRMPartnerImportModal';
import CRMCreditBulkUploadModal from './CRM/CRMCreditBulkUploadModal';
import StatementOfAccountsModal from './CRM/StatementOfAccountsModal';
import PaymentRemindersModal from '../pages/Invoicing/PaymentRemindersModal';
import HMSFloatingActionMenu from './ui/HMSFloatingActionMenu';
import HMSButton from './ui/HMSButton';
import HMSEmptyState from './ui/HMSEmptyState';
import HMSPageHeader from './ui/HMSPageHeader';
import HMSSkeleton from './ui/HMSSkeleton';
import { crmService } from '../services/crmService';
import { dashboardService } from '../services/dashboardService';
import { getApprovalBadge } from '../utils/crmStatusMapper';
import { useFlashMessage } from './FlashMessage';
import Layout from './Layout';
import Footer from './ui/Footer';
import './CRMDashboard.css';

const PAGE_SIZE = 50;

const CRMDashboard = () => {
    const history = useHistory();
    const { showSuccess, showError } = useFlashMessage();
    // ``profiles`` holds the rows currently rendered in the list. For
    // server-paginated tabs it grows in PAGE_SIZE chunks as the user clicks
    // "Load More". For the "needs-attention" tab it is derived client-side
    // from ``allProfiles`` (since ``has_risk_alerts`` is a computed field).
    const [profiles, setProfiles] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [allProfiles, setAllProfiles] = useState([]); // Drives the stat cards
    const [allProfilesLoaded, setAllProfilesLoaded] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('all');
    const searchInputRef = useRef(null);

    // Lightweight geocoded-partner pins for the "CRM Map" tab. Fetched once on
    // mount via a dedicated minimal endpoint (id/name/lat/lon/status/types) so
    // the tab can show the geocoded count in its label and render hundreds of
    // clustered markers without the heavy profile-list payload.
    const [mapPins, setMapPins] = useState([]);
    const [mapPinsLoading, setMapPinsLoading] = useState(true);
    const [mapPinsError, setMapPinsError] = useState(null);

    // Risk Alerts Popup state
    const [showRiskAlertsPopup, setShowRiskAlertsPopup] = useState(false);
    const [selectedProfileForAlerts, setSelectedProfileForAlerts] = useState(null);

    // Approval Status Modal state
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [selectedProfileForApproval, setSelectedProfileForApproval] = useState(null);
    const [isCRMApprover, setIsCRMApprover] = useState(false);
    const [csvImportOpen, setCsvImportOpen] = useState(false);
    const [creditCsvOpen, setCreditCsvOpen] = useState(false);
    const [soaModalOpen, setSoaModalOpen] = useState(false);
    // Ad-hoc risk check on a counterparty with no CRM record
    const [adHocRiskOpen, setAdHocRiskOpen] = useState(false);
    // Payment Reminders for credit approvers, who have no Financials module
    // (Marcel, 23.09: "Payment reminders visible for Amar").
    const [isCreditApprover, setIsCreditApprover] = useState(false);
    const [remindersOpen, setRemindersOpen] = useState(false);
    const [profilesRefreshKey, setProfilesRefreshKey] = useState(0);

    // Mirrors the active tab + debounced search so an in-flight Load More
    // request can detect if the user switched context mid-fetch and discard
    // the stale response instead of appending it to the new tab's list.
    const currentViewRef = useRef({ tab: 'all', search: '' });

    // Scroll to top on component mount
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Fetch user's CRM approver status on mount
    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                const userInfo = await dashboardService.getUserInfo();
                setIsCRMApprover(userInfo.is_crm_approver || false);
                setIsCreditApprover(userInfo.is_credit_approver || false);
            } catch (error) {
                console.error('Error fetching user info:', error);
                setIsCRMApprover(false);
                setIsCreditApprover(false);
            }
        };
        fetchUserInfo();
    }, []);

    // Fetch ALL profiles once on mount (active + archived) purely so the stat
    // cards on the left can show accurate counts. Runs in parallel with the
    // first paginated list fetch below, so it never blocks the list from
    // rendering its first 50 cards.
    const refreshAllProfilesForStats = useCallback(async () => {
        try {
            const [activeProfiles, archivedProfiles] = await Promise.all([
                crmService.getProfiles({ _t: Date.now() }),
                crmService.getProfiles({ show_archived: true, _t: Date.now() })
            ]);

            const activeData = Array.isArray(activeProfiles) ? activeProfiles : activeProfiles.results || [];
            const archivedData = Array.isArray(archivedProfiles) ? archivedProfiles : archivedProfiles.results || [];
            const combinedProfiles = [...activeData, ...archivedData];

            console.log('Stats fetch: active=', activeData.length, 'archived=', archivedData.length, 'total=', combinedProfiles.length);
            setAllProfiles(combinedProfiles);
            setAllProfilesLoaded(true);
        } catch (err) {
            console.error('Error fetching all CRM profiles for stats:', err);
            setAllProfilesLoaded(true);
        }
    }, []);

    useEffect(() => {
        refreshAllProfilesForStats();
    }, [refreshAllProfilesForStats, profilesRefreshKey]);

    // Fetch the geocoded map-pin projection once on mount (and after any
    // refresh, e.g. a bulk import). Runs in parallel with the stats/list
    // fetches and never blocks them.
    useEffect(() => {
        let cancelled = false;
        const fetchMapPins = async () => {
            try {
                setMapPinsLoading(true);
                const pins = await crmService.getMapPins();
                if (cancelled) return;
                setMapPins(pins);
                setMapPinsError(null);
            } catch (err) {
                if (cancelled) return;
                console.error('Error fetching CRM map pins:', err);
                setMapPinsError('Failed to load map data. Please try again.');
            } finally {
                if (!cancelled) setMapPinsLoading(false);
            }
        };
        fetchMapPins();
        return () => { cancelled = true; };
    }, [profilesRefreshKey]);

    // Debounce the search term so we only hit the server every 300ms while
    // the user types.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
        return () => clearTimeout(t);
    }, [searchTerm]);

    // Translate the active tab into query params for the CRM profiles endpoint.
    // ``needs-attention`` is intentionally NOT handled here; that tab is
    // computed client-side from ``allProfiles`` because ``has_risk_alerts``
    // is a serialiser-computed field that we cannot filter on at the DB level.
    const buildParamsForTab = useCallback((tab, search) => {
        const params = {};
        if (search) params.search = search;

        if (tab === 'all') {
            params.show_archived = 'all';
        } else if (tab === 'archived') {
            params.show_archived = true;
        } else if (tab === 'approved') {
            params.approval_status = 'approved';
        } else if (tab === 'pending') {
            params.approval_status = 'pending';
        } else if (tab === 'rejected') {
            params.approval_status = 'rejected';
        } else if (tab === 'hms-entities') {
            params.is_group_entity = 'true';
        }
        return params;
    }, []);

    const matchesNeedsAttentionSearch = useCallback((profile, term) => {
        if (!term) return true;
        const lower = term.toLowerCase();
        return (
            profile.legal_entity_name?.toLowerCase().includes(lower) ||
            profile.trading_short_name?.toLowerCase().includes(lower) ||
            (profile.partner_type_selections && profile.partner_type_selections.some(t => t.toLowerCase().includes(lower))) ||
            (profile.industry_selections && profile.industry_selections.some(i => i.toLowerCase().includes(lower))) ||
            profile.kyc_status_current?.toLowerCase().includes(lower) ||
            profile.aml_risk_level_current?.toLowerCase().includes(lower) ||
            profile.primary_contact?.contact_full_name?.toLowerCase().includes(lower) ||
            profile.primary_contact?.email_address?.toLowerCase().includes(lower)
        );
    }, []);

    // Server-paginated fetch for every tab except ``needs-attention``. Runs
    // whenever the active tab, search term, or refresh key changes and always
    // resets to the first page (offset=0).
    useEffect(() => {
        currentViewRef.current = { tab: activeTab, search: debouncedSearch };

        if (activeTab === 'needs-attention') return; // handled by the effect below
        if (activeTab === 'crm-map') { setLoading(false); return; } // map tab uses its own pin endpoint

        let cancelled = false;
        const fetchFirstPage = async () => {
            try {
                setLoading(true);
                const params = {
                    ...buildParamsForTab(activeTab, debouncedSearch),
                    limit: PAGE_SIZE,
                    offset: 0,
                };
                const data = await crmService.getProfiles(params);
                if (cancelled) return;

                // Backend now returns DRF paginated payload when limit/offset
                // are present: {count, next, previous, results}.
                const results = Array.isArray(data) ? data : (data.results || []);
                const count = (data && typeof data.count === 'number')
                    ? data.count
                    : results.length;

                setProfiles(results);
                setTotalCount(count);
                setError(null);
            } catch (err) {
                if (cancelled) return;
                console.error('Error fetching CRM profiles:', err);
                setError('Failed to load CRM profiles');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchFirstPage();
        return () => { cancelled = true; };
    }, [activeTab, debouncedSearch, profilesRefreshKey, buildParamsForTab]);

    // Client-side derivation for the "Needs Attention" tab. We piggy-back on
    // ``allProfiles`` (loaded for the stat cards) instead of a dedicated
    // fetch, so the user does not pay for a second round-trip.
    useEffect(() => {
        if (activeTab !== 'needs-attention') return;
        if (!allProfilesLoaded) {
            setLoading(true);
            return;
        }
        const matched = allProfiles.filter(p =>
            p.has_risk_alerts === true
            && !p.is_archived
            && matchesNeedsAttentionSearch(p, debouncedSearch)
        );
        setProfiles(matched);
        setTotalCount(matched.length);
        setLoading(false);
        setError(null);
    }, [activeTab, allProfiles, allProfilesLoaded, debouncedSearch, matchesNeedsAttentionSearch]);

    const handleProfileClick = (profileId) => {
        history.push(`/dashboard/crm/profile/${profileId}`);
    };

    const handleAddNewProfile = () => {
        history.push('/dashboard/crm/profile/new');
    };

    const handleLoadMore = async () => {
        // For server-paginated tabs, request the next batch of PAGE_SIZE rows
        // from the backend and append. ``needs-attention`` shows everything
        // up-front so the button is hidden for that tab.
        if (activeTab === 'needs-attention') return;
        if (loadingMore) return;

        const requestTab = activeTab;
        const requestSearch = debouncedSearch;

        try {
            setLoadingMore(true);
            const params = {
                ...buildParamsForTab(requestTab, requestSearch),
                limit: PAGE_SIZE,
                offset: profiles.length,
            };
            const data = await crmService.getProfiles(params);

            // If the user switched tabs / changed the search while we were
            // waiting, throw the response away rather than appending stale
            // rows to the now-different list.
            const view = currentViewRef.current;
            if (view.tab !== requestTab || view.search !== requestSearch) {
                console.log('Load more: view changed mid-flight, discarding response');
                return;
            }

            const results = Array.isArray(data) ? data : (data.results || []);
            const count = (data && typeof data.count === 'number') ? data.count : profiles.length + results.length;
            setProfiles(prev => [...prev, ...results]);
            setTotalCount(count);
        } catch (err) {
            console.error('Error loading more CRM profiles:', err);
            showError('Failed to load more partners. Please try again.');
        } finally {
            setLoadingMore(false);
        }
    };

    const handleRiskAlertsClick = (profile, event) => {
        event.stopPropagation(); // Prevent card click navigation
        setSelectedProfileForAlerts(profile);
        setShowRiskAlertsPopup(true);
    };

    const handleCloseRiskAlerts = () => {
        setShowRiskAlertsPopup(false);
        setSelectedProfileForAlerts(null);
    };

    const handleApprovalBadgeClick = (profile, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation(); // Prevent card click navigation
        }
        setSelectedProfileForApproval(profile);
        setShowApprovalModal(true);
    };

    const handleCloseApprovalModal = () => {
        setShowApprovalModal(false);
        setSelectedProfileForApproval(null);
    };

    // Enter/Space on the card and on the two chips inside it — all three are
    // `role="button"` on a non-button element, so the key handling a real
    // <button> gets for free has to be written out.
    //
    // preventDefault: Space would otherwise scroll the profiles list out from
    // under the keyboard user at the same moment the modal opens.
    // stopPropagation: the chips sit INSIDE the card, and a synthetic keydown
    // bubbles, so without this a chip's Enter would open its modal and then
    // navigate away to the profile. The pointer path already guards this the
    // same way, on click.
    const activateChipOnKey = (activate) => (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        event.stopPropagation();
        activate(event);
    };

    const handleStatusChange = async (statusData) => {
        try {
            const response = await crmService.changeApprovalStatus(
                selectedProfileForApproval.id,
                statusData
            );

            if (response.data.success) {
                showSuccess(response.data.message || 'Approval status updated successfully');
                // Bumping the refresh key re-runs the stats fetch AND the
                // paginated list fetch (resetting to the first 50 rows of
                // the current tab). Keeps state management simple.
                setProfilesRefreshKey(k => k + 1);
            }
        } catch (error) {
            throw new Error(error.message || 'Failed to update approval status');
        }
    };

    const getRiskBadgeClass = (score) => {
        if (score >= 8) return 'crm-risk-badge-excellent';
        if (score >= 6) return 'crm-risk-badge-good';
        if (score >= 4) return 'crm-risk-badge-moderate';
        return 'crm-risk-badge-poor';
    };

    const getApprovalStatusBadgeClass = (status) => {
        switch (status) {
            case 'approved': return 'crm-approval-badge-approved';
            case 'pending': return 'crm-approval-badge-pending';
            case 'rejected': return 'crm-approval-badge-rejected';
            default: return 'crm-approval-badge-pending';
        }
    };

    const getAmlBadgeClass = (level) => {
        switch (level) {
            case 'Low': return 'crm-aml-badge-low';
            case 'Medium': return 'crm-aml-badge-medium';
            case 'High': return 'crm-aml-badge-high';
            default: return 'crm-aml-badge-medium';
        }
    };

    const getSanctionStatus = (profile) => {
        // Check if HK sanction screening has been run
        if (profile.hk_last_screening_date) {
            // If suspect flag is true, it's sanctioned
            if (profile.hk_suspect_flag === true) {
                return 'Sanctioned';
            }
            // If screening was run and no suspect flag, it's cleared
            return 'Cleared';
        }
        
        // Legacy check for sanction_screening_result
        if (profile.sanction_screening_result) {
            const result = typeof profile.sanction_screening_result === 'string' 
                ? profile.sanction_screening_result.toLowerCase()
                : '';
            
            if (result.includes('clear') || result.includes('pass')) {
                return 'Cleared';
            }
            if (result.includes('sanction') || result.includes('match') || result.includes('hit')) {
                return 'Sanctioned';
            }
        }
        
        // No screening has been run
        return null;
    };

    const getSanctionBadgeClass = (status) => {
        if (status === 'Cleared') return 'crm-sanction-badge-cleared';
        if (status === 'Sanctioned') return 'crm-sanction-badge-sanctioned';
        return '';
    };

    // KYC portal status chip. Values come straight from the model
    // (``kyc_status_current``): Approved (green), Pending (amber), Flagged (red).
    const getKycBadgeClass = (status) => {
        switch (status) {
            case 'Approved': return 'crm-kyc-badge-approved';
            case 'Flagged': return 'crm-kyc-badge-flagged';
            case 'Pending': return 'crm-kyc-badge-pending';
            default: return 'crm-kyc-badge-pending';
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // With server-side pagination ``profiles`` already contains exactly the
    // rows we want to display (first 50, then appended in 50-row batches as
    // the user clicks "Load More").
    const displayedProfiles = profiles;
    const hasMore = !loading && activeTab !== 'needs-attention' && profiles.length < totalCount;

    // Calculate stat metrics from profiles data
    const allPartnersCount = allProfiles.filter(p => p.is_archived === false).length; // All non-archived
    const pendingPartnersCount = allProfiles.filter(p => p.approval_status === 'pending' && !p.is_archived).length;
    const approvedPartnersCount = allProfiles.filter(p => p.approval_status === 'approved' && !p.is_archived).length;
    const rejectedPartnersCount = allProfiles.filter(p => p.approval_status === 'rejected' && !p.is_archived).length;
    const archivedPartnersCount = allProfiles.filter(p => p.is_archived === true).length; // Always count ALL archived
    const partnersNeedAttentionCount = allProfiles.filter(p => p.has_risk_alerts === true && !p.is_archived).length;
    const hmsEntitiesCount = allProfiles.filter(p => p.is_group_entity === true && !p.is_archived).length;

    // Debug logging for stat metrics
    console.log('CRM Dashboard Stats:', {
        total: profiles.length,
        totalIncludingArchived: allProfiles.length,
        all: allPartnersCount,
        pending: pendingPartnersCount,
        approved: approvedPartnersCount,
        rejected: rejectedPartnersCount,
        archived: archivedPartnersCount,
        needsAttention: partnersNeedAttentionCount
    });
    
    // DEBUG: Check if approval_status field exists in profiles
    if (allProfiles.length > 0) {
        console.log('Sample profile approval_status:', allProfiles[0]?.approval_status);
        console.log('All profiles approval statuses:', allProfiles.map(p => ({
            name: p.legal_entity_name,
            approval_status: p.approval_status,
            is_archived: p.is_archived
        })));
    }

    // Tab tray for the shared page header. Only "All Partners" carries a
    // visible count today, so the other pills stay badge-free. The walkthrough's
    // "crm-tabs" step points at the whole tray, so the anchor sits on the
    // tray (tabs.dataTour) rather than on the first pill.
    const crmTabs = [
        { key: 'all', label: 'All Partners', icon: faUsers, count: loading ? '...' : allProfiles.length, showZero: true },
        { key: 'approved', label: 'Approved Partners', icon: faCheckCircle },
        { key: 'pending', label: 'Pending Partners', icon: faClock },
        { key: 'rejected', label: 'Rejected Partners', icon: faTimesCircle },
        { key: 'needs-attention', label: 'Needs Attention', icon: faExclamationTriangle },
        { key: 'hms-entities', label: 'IMT Entities', icon: faBuilding },
        { key: 'crm-map', label: 'CRM Map', icon: faMapMarkedAlt },
        { key: 'archived', label: 'Archived Partners', icon: faArchive },
    ];

    if (error) {
        return (
            <Layout showLeftNavSidebar={true} showRightSidebar={false}>
                <div className="crm-dashboard crm-error">
                    <div className="crm-error-message">{error}</div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout showLeftNavSidebar={true} showRightSidebar={false}>
            <div className="crm-dashboard" data-tour="crm-page">
                {/* Shared screen header: brand block, pill tray, search cluster */}
                <HMSPageHeader
                    title="Partner Management"
                    kicker="Partners"
                    className="crm-page-header"
                    tabs={{ items: crmTabs, activeKey: activeTab, onChange: setActiveTab, dataTour: 'crm-tabs' }}
                    actions={
                        <div className="crm-search-container" data-tour="crm-search">
                            <FontAwesomeIcon icon={faSearch} className="crm-search-icon" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Search profiles by name"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="crm-search-input"
                            />
                        </div>
                    }
                />

                {/* Main Content - Always Visible. On the map tab we hide the
                    left stat cards and let the map take the full width. */}
                <div className={`crm-main-content${activeTab === 'crm-map' ? ' crm-main-content--map' : ''}`}>
                    {/* Stats Cards Section - Left Third - Always Visible */}
                    <div className="crm-stats-section" data-tour="crm-stats">
                                <div className="crm-stat-card crm-stat-card-pending">
                                    <div className="crm-stat-icon crm-stat-pending">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </div>
                                    <div className="crm-stat-details">
                                        <div className="crm-stat-value">{loading ? '...' : pendingPartnersCount}</div>
                                        <div className="crm-stat-label">Pending Partners</div>
                                    </div>
                                </div>

                                <div className="crm-stat-card crm-stat-card-active">
                                    <div className="crm-stat-icon crm-stat-active">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </div>
                                    <div className="crm-stat-details">
                                        <div className="crm-stat-value">{loading ? '...' : approvedPartnersCount}</div>
                                        <div className="crm-stat-label">Approved Partners</div>
                                    </div>
                                </div>

                                <div className="crm-stat-card crm-stat-card-kyc">
                                    <div className="crm-stat-icon crm-stat-kyc">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M15 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H15M10 17L15 12M15 12L10 7M15 12H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </div>
                                    <div className="crm-stat-details">
                                        <div className="crm-stat-value">{loading ? '...' : rejectedPartnersCount}</div>
                                        <div className="crm-stat-label">Rejected Partners</div>
                                    </div>
                                </div>

                                <div className="crm-stat-card crm-stat-card-attention">
                                    <div className="crm-stat-icon crm-stat-attention">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                    </div>
                                    <div className="crm-stat-details">
                                        <div className="crm-stat-value">{loading ? '...' : partnersNeedAttentionCount}</div>
                                        <div className="crm-stat-label">Partners Need Attention</div>
                                    </div>
                                </div>

                                <div className="crm-stat-card crm-stat-card-hms-entities">
                                    <div className="crm-stat-icon crm-stat-hms-entities">
                                        <FontAwesomeIcon icon={faBuilding} />
                                    </div>
                                    <div className="crm-stat-details">
                                        <div className="crm-stat-value">{loading ? '...' : hmsEntitiesCount}</div>
                                        <div className="crm-stat-label">IMT Entities</div>
                                    </div>
                                </div>

                                <div className="crm-stat-card crm-stat-card-archived">
                                    <div className="crm-stat-icon crm-stat-archived">
                                        <FontAwesomeIcon icon={faArchive} />
                                    </div>
                                    <div className="crm-stat-details">
                                        <div className="crm-stat-value">{loading ? '...' : archivedPartnersCount}</div>
                                        <div className="crm-stat-label">Archived Partners</div>
                                    </div>
                                </div>
                            </div>

                            {/* Profile Cards Section - Right Two Thirds */}
                            <div className="crm-profiles-section">
                                {activeTab === 'crm-map' ? (
                                    <CRMDashboardMap
                                        pins={mapPins}
                                        loading={mapPinsLoading}
                                        error={mapPinsError}
                                    />
                                ) : (
                                <>
                                <div className="crm-profiles-grid" data-tour="crm-profiles-grid">
                                    {loading ? (
                                        // Loading skeleton cards that match actual profile card layout.
                                        // The card scaffold stays; every bar inside it is HMSSkeleton
                                        // now, so the shimmer and its reduced-motion behaviour come
                                        // from the primitive instead of a local @keyframes.
                                        [...Array(6)].map((_, index) => (
                                            <div key={index} className="crm-profile-card crm-loading-card">
                                                <div className="crm-card-header">
                                                    <HMSSkeleton shape="rect" width={140} height={20} />
                                                    <HMSSkeleton shape="rect" width={80} height={24} />
                                                </div>
                                                <div className="crm-card-content">
                                                    <HMSSkeleton lines={3} />
                                                </div>
                                                <div className="crm-card-footer">
                                                    <HMSSkeleton shape="rect" width={100} height={12} />
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        displayedProfiles.map((profile) => {
                                            const sanctionStatus = getSanctionStatus(profile);
                                            const approvalBadge = getApprovalBadge(profile);
                                            return (
                                <div
                                    key={profile.id}
                                    className={`crm-profile-card ${profile.is_archived ? 'archived' : ''}`}
                                    role="button"
                                    tabIndex={0}
                                    // Without a label the name of a role="button" is computed from
                                    // its contents, i.e. the whole card read aloud. Name it for the
                                    // one thing activating it does.
                                    aria-label={`Open ${profile.legal_entity_name} profile`}
                                    onClick={() => handleProfileClick(profile.id)}
                                    onKeyDown={activateChipOnKey(() => handleProfileClick(profile.id))}
                                >
                                    {/* Header: partner name + legal/trading name + status badge */}
                                    <div className="crm-card-header">
                                        <div className="crm-profile-names">
                                            <h3 className="crm-legal-name">{profile.legal_entity_name}</h3>
                                            {profile.trading_short_name && (
                                                <span className="crm-trading-name">{profile.trading_short_name}</span>
                                            )}
                                        </div>
                                        <div className="crm-status-badges">
                                            {profile.is_archived ? (
                                                <span className="crm-archived-badge">
                                                    <FontAwesomeIcon icon={faArchive} />
                                                    Archived
                                                </span>
                                            ) : (
                                                <div className="crm-status-badge-container">
                                                    <span
                                                        className={`crm-approval-badge ${getApprovalStatusBadgeClass(profile.approval_status)} crm-approval-badge-clickable`}
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            handleApprovalBadgeClick(profile, e);
                                                        }}
                                                        onKeyDown={activateChipOnKey((e) => handleApprovalBadgeClick(profile, e))}
                                                        title={approvalBadge.title || 'View approval status & history'}
                                                    >
                                                        {approvalBadge.label}
                                                    </span>
                                                    {profile.pending_rfc_count > 0 && (
                                                        <span className="rfc-submitted-indicator">RFC submitted</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Stat chips: Rating, AML, KYC, Sanction (rendered gracefully when absent) */}
                                    <div className="crm-chip-row">
                                        <span
                                            className={`crm-chip ${getRiskBadgeClass(profile.overall_risk_rating_score)}`}
                                            title="Overall risk rating"
                                        >
                                            <span className="crm-chip-label">Rating</span>
                                            <span className="crm-chip-value">{profile.overall_risk_rating_score}/10</span>
                                        </span>
                                        {profile.aml_risk_level_current && (
                                            <span
                                                className={`crm-chip ${getAmlBadgeClass(profile.aml_risk_level_current)}`}
                                                title="AML risk level"
                                            >
                                                <span className="crm-chip-label">AML</span>
                                                <span className="crm-chip-value">{profile.aml_risk_level_current}</span>
                                            </span>
                                        )}
                                        {profile.kyc_status_current && (
                                            <span
                                                className={`crm-chip ${getKycBadgeClass(profile.kyc_status_current)}`}
                                                title="KYC portal status"
                                            >
                                                <span className="crm-chip-label">KYC</span>
                                                <span className="crm-chip-value">{profile.kyc_status_current}</span>
                                            </span>
                                        )}
                                        {sanctionStatus && (
                                            <span
                                                className={`crm-chip ${getSanctionBadgeClass(sanctionStatus)}`}
                                                title="Sanction screening"
                                            >
                                                <FontAwesomeIcon
                                                    icon={sanctionStatus === 'Cleared' ? faShieldAlt : faExclamationTriangle}
                                                    className="crm-chip-icon"
                                                />
                                                <span className="crm-chip-value">{sanctionStatus}</span>
                                            </span>
                                        )}
                                    </div>

                                    {/* Labeled detail rows with icons */}
                                    <div className="crm-card-content">
                                        <div className="crm-profile-info">
                                            <div className="crm-info-row">
                                                <FontAwesomeIcon icon={faTags} className="crm-info-icon" />
                                                <span className="crm-info-label">Types</span>
                                                <span className="crm-info-value">
                                                    {(profile.partner_type_selections || []).join(', ') || '—'}
                                                </span>
                                            </div>
                                            <div className="crm-info-row">
                                                <FontAwesomeIcon icon={faIndustry} className="crm-info-icon" />
                                                <span className="crm-info-label">Industries</span>
                                                <span className="crm-info-value">
                                                    {(profile.industry_selections || []).join(', ') || '—'}
                                                </span>
                                            </div>
                                            {profile.primary_contact && (
                                                <div className="crm-info-row">
                                                    <FontAwesomeIcon icon={faUser} className="crm-info-icon" />
                                                    <span className="crm-info-label">Contact</span>
                                                    <span className="crm-info-value">
                                                        {profile.primary_contact?.contact_full_name || 'No Contact Available'}
                                                        {profile.primary_contact?.contact_role_title &&
                                                            ` (${profile.primary_contact.contact_role_title})`
                                                        }
                                                    </span>
                                                </div>
                                            )}
                                            {profile.last_transaction_date && (
                                                <div className="crm-info-row">
                                                    <FontAwesomeIcon icon={faReceipt} className="crm-info-icon" />
                                                    <span className="crm-info-label">Last Txn</span>
                                                    <span className="crm-info-value">
                                                        {formatDate(profile.last_transaction_date)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Subtle footer: updated date + risk-alerts icon */}
                                    <div className="crm-card-footer">
                                        <div className="crm-card-footer-meta">
                                            <div className="crm-card-updated">
                                                <FontAwesomeIcon icon={faClock} className="crm-card-updated-icon" />
                                                Updated {formatDate(profile.updated_at_timestamp)}
                                            </div>
                                            {profile.has_risk_alerts && (
                                                <span
                                                    className="crm-attention-badge"
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={(e) => handleRiskAlertsClick(profile, e)}
                                                    onKeyDown={activateChipOnKey((e) => handleRiskAlertsClick(profile, e))}
                                                    // The chip is icon-only, so `title` was carrying the
                                                    // name too — it stays for the pointer tooltip, but the
                                                    // label is what assistive tech reads.
                                                    aria-label="View risk alerts"
                                                    title="Click to view risk alerts"
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faExclamationTriangle}
                                                        className="crm-attention-icon"
                                                    />
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                            );
                                        })
                                    )}
                                </div>

                                {hasMore && (
                                    <div className="crm-load-more-section">
                                        {/* The only button on this screen that is not a tab: a
                                            brand CTA hand-rolled at .crm-load-more-btn, which is
                                            exactly the primitive's `primary`. */}
                                        <HMSButton
                                            variant="primary"
                                            onClick={handleLoadMore}
                                            disabled={loadingMore}
                                        >
                                            {loadingMore
                                                ? 'Loading...'
                                                : `Load ${Math.min(PAGE_SIZE, totalCount - profiles.length)} More Profiles (${totalCount - profiles.length} remaining)`}
                                        </HMSButton>
                                    </div>
                                )}

                                {profiles.length === 0 && !loading && (
                                    /* No `variant` — the block this replaces sat straight on
                                       the page gradient with no surface of its own. The
                                       magnifier keeps its own 64px box; it is a hand-drawn
                                       SVG, not an icon-font glyph the 48px slot can scale. */
                                    <HMSEmptyState
                                        icon={(
                                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        )}
                                        title={debouncedSearch
                                            ? 'No profiles found'
                                            : 'CRM profiles will display here when added'}
                                        hint={debouncedSearch
                                            ? 'No CRM profiles match your search criteria. Try adjusting your search terms.'
                                            : undefined}
                                    />
                                )}
                                </>
                                )}
                            </div>
                        </div>

                <HamsterFloatingButton />

                {/* Risk Alerts Popup */}
                <RiskAlertsPopup
                    isOpen={showRiskAlertsPopup}
                    onClose={handleCloseRiskAlerts}
                    partnerName={selectedProfileForAlerts?.legal_entity_name || ''}
                    partnerId={selectedProfileForAlerts?.id}
                    redFlags={selectedProfileForAlerts?.latest_red_flags}
                    legalDisputes={selectedProfileForAlerts?.latest_legal_disputes}
                    sanctionScreening={selectedProfileForAlerts?.latest_sanction_screening}
                />

                {/* Approval Status Modal */}
                <ApprovalStatusModal
                    isOpen={showApprovalModal}
                    onClose={handleCloseApprovalModal}
                    profile={selectedProfileForApproval}
                    isCRMApprover={isCRMApprover}
                    onStatusChange={handleStatusChange}
                />

                {/* Hamster Chat Integration */}
                <HamsterFloatingButton />

                {/* Back to Top Button */}
                <BackToTopButton />

                {/* Partner Bulk Import Modal */}
                <CRMPartnerImportModal
                    isOpen={csvImportOpen}
                    onClose={() => setCsvImportOpen(false)}
                    onImported={(data) => {
                        if (data?.created > 0) {
                            showSuccess(data.message);
                            setProfilesRefreshKey(k => k + 1);
                        }
                    }}
                />

                {/* Bulk credit-terms upload. Separate from Import Partners on
                    purpose: that one CREATES partners, this one UPDATES credit on
                    partners we already hold, and shows the plan before writing. */}
                <CRMCreditBulkUploadModal
                    isOpen={creditCsvOpen}
                    onClose={() => setCreditCsvOpen(false)}
                    onApplied={(data) => {
                        if (data?.updated > 0) {
                            showSuccess(data.message);
                            setProfilesRefreshKey(k => k + 1);
                        }
                    }}
                />

                {/* Statement of Accounts Modal (approver-only entry point from the FAB) */}
                <StatementOfAccountsModal
                    isOpen={soaModalOpen}
                    onClose={() => setSoaModalOpen(false)}
                />

                {/* Ad-hoc sanctions + AI risk check on a non-partner. Creates no
                    profile, so the partner list stays clean; the report lands in
                    Knowledge Hub → General → Partner Info. */}
                <AdHocRiskCheckModal
                    isOpen={adHocRiskOpen}
                    onClose={() => setAdHocRiskOpen(false)}
                />

                {/* The same lightbox as Outgoing Invoices, without the invoice
                    actions a credit approver cannot open. */}
                <PaymentRemindersModal
                    isOpen={remindersOpen}
                    onClose={() => setRemindersOpen(false)}
                    showActions={false}
                />

                {/* Floating action menu - replaces the old stacked header buttons.
                    Statement of Accounts and Import Partners are gated to approvers
                    via the `hidden` flag (matches the previous behaviour). */}
                <HMSFloatingActionMenu
                    label="Actions"
                    items={[
                        {
                            key: 'add-profile',
                            label: 'Add New CRM Profile',
                            icon: faUserPlus,
                            color: 'blue',
                            onClick: handleAddNewProfile,
                            title: 'Create a new CRM partner profile',
                        },
                        {
                            key: 'import-partners',
                            label: 'Import Partners',
                            icon: faUpload,
                            color: 'green',
                            onClick: () => setCsvImportOpen(true),
                            title: 'Import multiple partners from a CSV file',
                            hidden: !isCRMApprover,
                        },
                        {
                            key: 'update-credit-terms',
                            label: 'Update Credit Terms',
                            icon: faUpload,
                            color: 'teal',
                            onClick: () => setCreditCsvOpen(true),
                            title: 'Update credit days, limits and risk ratings on existing partners from a CSV',
                            hidden: !isCRMApprover,
                        },
                        {
                            key: 'statement-of-accounts',
                            label: 'Statement of Accounts',
                            icon: faFileInvoiceDollar,
                            color: 'purple',
                            onClick: () => setSoaModalOpen(true),
                            title: 'Generate a Statement of Accounts PDF for any partner',
                            hidden: !isCRMApprover,
                        },
                        {
                            key: 'payment-reminders',
                            label: 'Payment Reminders',
                            icon: faBell,
                            color: 'orange',
                            onClick: () => setRemindersOpen(true),
                            title: 'Chase overdue and soon-due customer invoices by email',
                            hidden: !isCreditApprover,
                        },
                        {
                            key: 'kyc-onboarding',
                            label: 'KYC Onboarding',
                            icon: faFileShield,
                            color: 'teal',
                            onClick: () => history.push('/dashboard/crm/kyc'),
                            title: 'Invite partners to the KYC portal and review their submissions',
                        },
                        {
                            key: 'adhoc-risk-check',
                            label: 'Ad-hoc Risk Check',
                            icon: faShieldAlt,
                            color: 'orange',
                            onClick: () => setAdHocRiskOpen(true),
                            title: 'Screen a counterparty that is not yet a partner — no profile is created',
                        },
                    ]}
                />

                {/* Footer */}
                <Footer />
            </div>
        </Layout>
    );
};

export default CRMDashboard; 